import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { getHosthistory } from "../store/history/history.action";
import useTranslation from "../hooks/useTranslation";
import { groupNumber } from "../util/groupNumber";

// Parsed as local midnight: `new Date("2026-10-08")` shifts a day back for
// viewers west of Greenwich. Mirrors the DailyIncome list on the dashboard.
const dayLabel = (value, locale) => {
  const [y, m, d] = String(value || "").split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(y, m - 1, d));
};

const HostHistory = () => {
  const { hostHitory } = useSelector((state) => state.history);
  const { t, languageCode } = useTranslation();
  // Latin digits in every language, so the ledger stays comparable across locales.
  const locale = `${languageCode || "en"}-u-nu-latn`;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Was reading the host id out of router state, which no caller ever set, and
  // passing it in the month slot — the page always rendered "No data found" on a
  // direct visit. Read the id the same way every other host screen does.
  const hostId = localStorage.getItem("hostId");
  const month = moment().format("YYYY-MM");
  // The back arrow used window.history.back(), which leaves the portal entirely
  // when the page was opened directly from a link. Always return to the dashboard.
  const goBack = () => navigate("/host/homePage");

  useEffect(() => {
    // getHosthistory(month, hostId) — the old call passed four arguments with the
    // first two reversed, so the endpoint got month=<hostId>.
    dispatch(getHosthistory(month, hostId));
  }, [dispatch, hostId, month]);

  return (
    <>
      <div className="page-content">
        <div className="main-wrapper">
          <div className="main-section p-2">
            <div className="row mb-2">
              <div className="col-4 d-flex align-items-center">
                <i
                  className="fa-solid fa-arrow-left text-dark"
                  style={{ fontSize: "16px" }}
                  onClick={goBack}
                ></i>
              </div>
              <div className="col-4 text-center">
                <p
                  className="text-dark fw-bold mb-0"
                  style={{ fontSize: "16px", fontWeight: "500" }}
                >
                  {t("history")}
                </p>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between">
              <h4
                className="text-dark fw-bold pt-2 mt-2"
                style={{ fontSize: "15px" }}
              >
                {t("host_history")}
              </h4>
              <div></div>
            </div>

            <div className="agency-detail">
              {hostHitory?.length > 0 ? (
                // The endpoint returns the whole month in one shot (no start/limit),
                // so there is nothing to page through — InfiniteScroll here only
                // produced a permanently-empty second fetch.
                <div className="row">
                    {hostHitory?.map((data, index) => {
                      // The endpoint returns { date: "YYYY-MM-DD", totalRCoin }.
                      // The old rows read data.coin and split date on a comma, so
                      // every cell rendered blank even when the day had income.
                      return (
                        <div className="col-12 mb-2" key={data?.date || index}>
                          <div className="agency-invitation bg-white p-2">
                            <div className="row">
                              <div className="col-6 text-center">
                                <span
                                  style={{
                                    color: "var(--mx-muted)",
                                    fontSize: "8px",
                                  }}
                                >
                                  {t("Coin Income")}
                                </span>
                                <div className="d-flex align-items-center justify-content-center">
                                  <img
                                    src={require("../assets/images/rcoin.png")}
                                    style={{ height: "15px", width: "15px" }}
                                    alt=""
                                  />
                                  <span
                                    className="mb-0 ms-1 fw-bolder"
                                    style={{
                                      color: "var(--mx-warn)",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {groupNumber(data?.totalRCoin, languageCode)}
                                  </span>
                                </div>
                              </div>
                              <div className="col-6 text-center">
                                <span
                                  style={{
                                    color: "var(--mx-muted)",
                                    fontSize: "8px",
                                  }}
                                >
                                  {t("date")}
                                </span>
                                <div className="">
                                  <p
                                    className="mb-0 ms-1 "
                                    style={{
                                      color: "var(--mx-text)",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {dayLabel(data?.date, locale)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="d-flex justify-content-center align-items-center my-4">
                  <span>{t("No data found")}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HostHistory;
